import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { SupportedLanguageValue } from '@ballerine/common';
import { SUPPORTED_LANGUAGES } from '@ballerine/common';

declare global {
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions -- interface augmentation
  interface Window {
    google?: {
      translate?: {
        TranslateElement: new (options: unknown, id: string) => unknown;
      };
    };
    googleTranslateElementInit?: () => void;
  }
}

export type LanguageOption = {
  label: string;
  value: string;
};

export const GOOGLE_TRANSLATE_SCRIPT_ID = 'google-translate-script';
export const GOOGLE_TRANSLATE_ELEMENT_ID = 'google_translate_element';
export const GOOGLE_TRANSLATE_COOKIE = 'googtrans';

export const LANGUAGE_OPTIONS: readonly LanguageOption[] = SUPPORTED_LANGUAGES;

export type LanguageValue = SupportedLanguageValue;

export const DEFAULT_LANGUAGE: LanguageValue = SUPPORTED_LANGUAGES[0].value;

const getPossibleCookieDomains = (): string[] => {
  if (typeof window === 'undefined') {
    return [''];
  }

  const hostname = window.location.hostname;
  const segments = hostname.split('.');
  const domains = new Set<string>(['', hostname, `.${hostname}`]);

  for (let i = 1; i < segments.length; i++) {
    const candidate = segments.slice(i).join('.');
    domains.add(candidate);
    domains.add(`.${candidate}`);
  }

  return Array.from(domains);
};

const getPossibleCookiePaths = (): string[] => {
  if (typeof window === 'undefined') {
    return ['/'];
  }

  const pathname = window.location.pathname || '/';

  if (pathname === '/' || pathname === '') {
    return ['/'];
  }

  const paths = new Set<string>(['/']);
  const segments = pathname.split('/').filter(Boolean);
  let current = '';

  segments.forEach(segment => {
    current += `/${segment}`;
    paths.add(current);
    paths.add(`${current}/`);
  });

  return Array.from(paths);
};

const getLanguageFromCookie = (
  defaultLanguage: LanguageValue,
  languages: readonly LanguageOption[],
): LanguageValue => {
  if (typeof document === 'undefined') {
    return defaultLanguage;
  }

  const cookie = document.cookie
    .split('; ')
    .find(row => row.startsWith(`${GOOGLE_TRANSLATE_COOKIE}=`));

  if (!cookie) {
    return defaultLanguage;
  }

  const value = decodeURIComponent(cookie.split('=')[1] ?? '');
  const parts = value.split('/').filter(Boolean);
  const cookieLanguage = parts.at(-1);

  if (!cookieLanguage) {
    return defaultLanguage;
  }

  return languages.some(({ value: optionValue }) => optionValue === cookieLanguage)
    ? (cookieLanguage as LanguageValue)
    : defaultLanguage;
};

const clearGoogleTranslateCookies = () => {
  if (typeof document === 'undefined') {
    return;
  }

  const cookieBase = `${GOOGLE_TRANSLATE_COOKIE}=; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  const domains = getPossibleCookieDomains();
  const paths = getPossibleCookiePaths();

  paths.forEach(path => {
    domains.forEach(domain => {
      const domainPart = domain ? `; domain=${domain}` : '';
      document.cookie = `${cookieBase}; path=${path}${domainPart}`;
    });
  });
};

type UseGoogleTranslateLanguageSelectorOptions = {
  languages?: readonly LanguageOption[];
  defaultLanguage?: LanguageValue;
  protectedTerms?: string[];
  enabled?: boolean;
  initialLanguage?: LanguageValue;
};

export const useGoogleTranslateLanguageSelector = ({
  languages = LANGUAGE_OPTIONS,
  defaultLanguage = DEFAULT_LANGUAGE,
  protectedTerms = [],
  enabled = true,
  initialLanguage,
}: UseGoogleTranslateLanguageSelectorOptions = {}) => {
  const [isTranslateReady, setIsTranslateReady] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<LanguageValue>(
    () => initialLanguage ?? getLanguageFromCookie(defaultLanguage, languages),
  );
  const translateElementInitializedRef = useRef(false);
  const normalizedProtectedTerms = useMemo(() => {
    const unique = new Set(
      protectedTerms.map(term => term.trim()).filter((term): term is string => Boolean(term)),
    );

    return Array.from(unique);
  }, [protectedTerms]);

  const googleLanguageList = useMemo(
    () =>
      languages.filter(language => language.value !== defaultLanguage).map(({ value }) => value),
    [defaultLanguage, languages],
  );

  const loadTranslateElement = useCallback(() => {
    if (!enabled) {
      setIsTranslateReady(false);

      return;
    }

    let comboPollTimeout: number | null = null;

    const waitForCombo = () => {
      const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');

      if (combo) {
        setIsTranslateReady(true);

        return;
      }

      comboPollTimeout = window.setTimeout(waitForCombo, 250);
    };

    const initializeTranslateElement = () => {
      if (translateElementInitializedRef.current || !window.google?.translate?.TranslateElement) {
        waitForCombo();

        return;
      }

      new window.google.translate.TranslateElement(
        {
          pageLanguage: 'en',
          autoDisplay: false,
          includedLanguages: googleLanguageList.join(','),
        },
        GOOGLE_TRANSLATE_ELEMENT_ID,
      );

      translateElementInitializedRef.current = true;
      waitForCombo();
    };

    if (window.google?.translate?.TranslateElement) {
      initializeTranslateElement();
    } else {
      window.googleTranslateElementInit = initializeTranslateElement;

      if (!document.getElementById(GOOGLE_TRANSLATE_SCRIPT_ID)) {
        const script = document.createElement('script');
        script.id = GOOGLE_TRANSLATE_SCRIPT_ID;
        script.src =
          'https://translate.google.com/translate_a/element.js?cb=googleTranslateElementInit';
        script.async = true;
        document.body.appendChild(script);
      }
    }

    return () => {
      if (comboPollTimeout) {
        window.clearTimeout(comboPollTimeout);
      }
    };
  }, [enabled, googleLanguageList]);

  useEffect(loadTranslateElement, [loadTranslateElement]);

  useEffect(() => {
    if (!enabled || !isTranslateReady) {
      return;
    }

    const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');

    if (!combo) {
      return;
    }

    const googleValue = selectedLanguage === defaultLanguage ? '' : selectedLanguage;

    if (combo.value === googleValue) {
      return;
    }

    combo.value = googleValue;
    combo.dispatchEvent(new Event('change', { bubbles: true }));

    if (googleValue === '') {
      document.cookie = 'googtrans=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/;';
    }
  }, [defaultLanguage, isTranslateReady, selectedLanguage]);

  useEffect(() => {
    if (!enabled || typeof document === 'undefined') {
      return;
    }

    if (!normalizedProtectedTerms.length) {
      return;
    }

    const PROTECTED_ATTR = 'data-translation-protected';

    const wrapMatchesWithProtectedSpan = (node: Node, term: string) => {
      if (node.nodeType !== Node.TEXT_NODE || !node.textContent?.length) {
        return;
      }

      const parentElement = node.parentElement;

      if (!parentElement) {
        return;
      }

      if (parentElement.closest(`[${PROTECTED_ATTR}]`)) {
        return;
      }

      const textContent = node.textContent;
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedTerm})`, 'gi');
      let match: RegExpExecArray | null;
      let lastIndex = 0;
      let hasMatch = false;
      const fragment = document.createDocumentFragment();

      while ((match = regex.exec(textContent)) !== null) {
        hasMatch = true;

        if (match.index > lastIndex) {
          fragment.appendChild(document.createTextNode(textContent.slice(lastIndex, match.index)));
        }

        const protectedSpan = document.createElement('span');
        protectedSpan.textContent = match[0];
        protectedSpan.classList.add('notranslate');
        protectedSpan.setAttribute('translate', 'no');
        protectedSpan.setAttribute(PROTECTED_ATTR, 'true');

        fragment.appendChild(protectedSpan);

        lastIndex = match.index + match[0].length;
      }

      if (!hasMatch) {
        return;
      }

      if (lastIndex < textContent.length) {
        fragment.appendChild(document.createTextNode(textContent.slice(lastIndex)));
      }

      parentElement.replaceChild(fragment, node);
    };

    const protectTermsInDom = () => {
      const body = document.body;

      if (!body) {
        return;
      }

      normalizedProtectedTerms.forEach(term => {
        const lowerTerm = term.toLowerCase();

        const walker = document.createTreeWalker(body, NodeFilter.SHOW_TEXT, {
          acceptNode: node => {
            if (!node.textContent?.trim()) {
              return NodeFilter.FILTER_REJECT;
            }

            if (
              node.parentElement?.hasAttribute(PROTECTED_ATTR) ||
              node.parentElement?.closest(`[${PROTECTED_ATTR}]`)
            ) {
              return NodeFilter.FILTER_REJECT;
            }

            return node.textContent.toLowerCase().includes(lowerTerm)
              ? NodeFilter.FILTER_ACCEPT
              : NodeFilter.FILTER_REJECT;
          },
        });

        const matches: Node[] = [];

        while (walker.nextNode()) {
          matches.push(walker.currentNode);
        }

        matches.forEach(node => {
          wrapMatchesWithProtectedSpan(node, term);
        });
      });
    };

    protectTermsInDom();

    const observer = new MutationObserver(() => {
      protectTermsInDom();
    });

    observer.observe(document.body, { subtree: true, childList: true });

    return () => {
      observer.disconnect();
    };
  }, [normalizedProtectedTerms]);

  useEffect(() => {
    if (!enabled) {
      setSelectedLanguage(defaultLanguage);

      return;
    }

    if (initialLanguage) {
      setSelectedLanguage(initialLanguage);

      return;
    }

    setSelectedLanguage(getLanguageFromCookie(defaultLanguage, languages));
  }, [defaultLanguage, enabled, initialLanguage, languages]);

  const clearTranslationState = useCallback(() => {
    if (!enabled) {
      return;
    }

    clearGoogleTranslateCookies();
    setSelectedLanguage(defaultLanguage);

    if (typeof document !== 'undefined') {
      const combo = document.querySelector<HTMLSelectElement>('.goog-te-combo');

      if (combo) {
        combo.value = '';
        combo.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }, [defaultLanguage, enabled]);

  const resetToDefaultLanguage = useCallback(() => {
    clearTranslationState();
    window.location.reload();
  }, [clearTranslationState]);

  useEffect(() => {
    if (!enabled || !isTranslateReady) {
      return;
    }

    // Google Translate manipulates the DOM in a way that breaks the app. This is a workaround to prevent the app from breaking. i.e. table sorting throws an error.
    if (typeof Node === 'function' && Node.prototype) {
      // @ts-ignore
      const originalRemoveChild = Node.prototype.removeChild;
      // @ts-ignore
      Node.prototype.removeChild = function (child) {
        if (child.parentNode !== this) {
          if (console) {
            console.error('Cannot remove a child from a different parent', child, this);
          }

          return child;
        }

        return originalRemoveChild.apply(
          this,
          // @ts-ignore
          // eslint-disable-next-line prefer-rest-params -- arguments is a valid argument
          arguments,
        );
      };

      // @ts-ignore
      const originalInsertBefore = Node.prototype.insertBefore;
      // @ts-ignore
      Node.prototype.insertBefore = function (newNode, referenceNode) {
        if (referenceNode && referenceNode.parentNode !== this) {
          if (console) {
            console.error(
              'Cannot insert before a reference node from a different parent',
              referenceNode,
              this,
            );
          }

          return newNode;
        }

        return originalInsertBefore.apply(
          this,
          // @ts-ignore
          // eslint-disable-next-line prefer-rest-params -- arguments is a valid argument
          arguments,
        );
      };
    }
  }, [enabled, isTranslateReady]);

  return {
    languages,
    selectedLanguage,
    isTranslateReady: enabled ? isTranslateReady : false,
    setSelectedLanguage,
    translateElementId: GOOGLE_TRANSLATE_ELEMENT_ID,
    defaultLanguage,
    clearTranslationState,
    resetToDefaultLanguage,
  };
};
